import { type IkeClientService, Services } from './index';

export interface ILocalStorage extends IkeClientService {
	get<T>(key: string): T;
	getItem<T>(key: string): T;
	set<T>(key: string, value: T): void;
	setItem<T>(key: string, value: T): void;
	delete(key: string): void;
	removeItem(key: string): void;
	size(): number;
	keys(): string[];
	values(): any[];
	length: number;
	clear(): void;
	storageKey(key: string): string | null;
}

function storageKey(key: string): string | null {
	if (Services.Config.env == null) {
		throw new Error('No ApplicationId');
	}
	return ['Parse', `dragon-${Services.Config.env}`, key].join('/');
}

function resolveKey(key: string): string {
	return storageKey(key) ?? key;
}

export const LocalStorage: ILocalStorage = {
	__priority: 10,

	storageKey,

	get<T>(key: string): T {
		return this.getItem<T>(key);
	},

	getItem<T>(key: string): T {
		const resolved = resolveKey(key);
		const raw = localStorage.getItem(resolved);
		if (raw == null) {
			return null as T;
		}
		try {
			return JSON.parse(raw) as T;
		} catch {
			return raw as unknown as T;
		}
	},

	set<T>(key: string, value: T): void {
		this.setItem(key, value);
	},

	setItem<T>(key: string, value: T): void {
		const resolved = resolveKey(key);
		localStorage.setItem(resolved, JSON.stringify(value));
	},

	delete(key: string): void {
		this.removeItem(key);
	},

	removeItem(key: string): void {
		const resolved = resolveKey(key);
		localStorage.removeItem(resolved);
	},

	size(): number {
		return localStorage.length;
	},

	keys(): string[] {
		const result: string[] = [];
		for (let i = 0; i < localStorage.length; i++) {
			result.push(localStorage.key(i)!);
		}
		return result;
	},

	values(): any[] {
		return this.keys().map((key) => localStorage.getItem(key));
	},

	get length(): number {
		return localStorage.length;
	},

	clear(): void {
		localStorage.clear();
	},
};
